import React, { useState, useEffect } from 'react';
import { systemSettingsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import '../Dashboard/Dashboard.css';
import '../Integrations/Integrations.css';
import './Settings.css';

const EMPTY_FORM = {
    whatsapp: {
        provider: '',
        meta: { phoneId: '', accessToken: '', templateName: 'otp_login', templateLang: 'en_US' },
        msg91: { whatsappNumber: '', templateName: 'otp_verification', templateLang: 'en' },
    },
    sms: {
        provider: '',
        msg91: { templateId: '', senderId: 'HTWIFI' },
        twilio: { accountSid: '', authToken: '', phoneNumber: '' },
        ask4sms: {
            baseUrl: 'http://api.ask4sms.in/sms/1/text/query', username: '', password: '',
            senderId: '', templateId: '', peId: '', tmId: '', messageTemplate: '',
        },
    },
    msg91AuthKey: '',
};

const Settings = () => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [masked, setMasked]     = useState({}); // which secret fields currently have a saved value
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [testMobile, setTestMobile] = useState('');
    const [testMethod, setTestMethod] = useState('whatsapp');
    const [testing, setTesting]       = useState(false);
    // The last test's outcome, kept on screen — the provider's reason is the fix.
    const [testResult, setTestResult] = useState(null);

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await systemSettingsAPI.get();
            const d = res.data.data;
            setFormData({
                whatsapp: {
                    provider: d.whatsapp.provider || '',
                    meta: { ...d.whatsapp.meta, accessToken: '' }, // never pre-fill secrets — masked indicator shown separately
                    msg91: { ...d.whatsapp.msg91 },
                },
                sms: {
                    provider: d.sms.provider || '',
                    msg91: { ...d.sms.msg91 },
                    twilio: { ...d.sms.twilio, authToken: '' },
                    ask4sms: { ...EMPTY_FORM.sms.ask4sms, ...(d.sms.ask4sms || {}), password: '' },
                },
                msg91AuthKey: '',
            });
            setMasked({
                metaAccessToken: !!d.whatsapp.meta.accessToken,
                twilioAuthToken: !!d.sms.twilio.authToken,
                ask4smsPassword: !!d.sms.ask4sms?.password,
                msg91AuthKey: !!d.msg91AuthKey,
            });
            // Test the channel that is set up: SMS when there is no WhatsApp sender.
            if (!d.whatsapp.provider && d.sms.provider) setTestMethod('sms');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await systemSettingsAPI.update(formData);
            toast.success('Settings saved — takes effect on the next OTP send, no restart needed');
            fetchSettings();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleTestOtp = async (e) => {
        e.preventDefault();
        if (!testMobile.trim()) return toast.error('Enter a mobile number to test');
        setTesting(true);
        setTestResult(null);
        try {
            await systemSettingsAPI.testOtp({ mobile: testMobile.trim(), method: testMethod });
            const message = `Test OTP sent via ${testMethod === 'sms' ? 'SMS' : 'WhatsApp'} — check the phone`;
            toast.success(message);
            setTestResult({ ok: true, message });
        } catch (err) {
            const message = err.response?.data?.message || 'Test send failed';
            toast.error(message);
            setTestResult({ ok: false, message });
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <div className="page-container-modern">
                <div className="loading-state-modern">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div>
                    <h1 className="page-title-modern">
                        <i className="fas fa-cog" style={{ marginRight: 10, color: '#6366f1' }}></i>
                        Platform Settings
                    </h1>
                    <p className="page-subtitle-modern">
                        One shared OTP sender for every branch across every tenant — not per-branch or per-tenant configuration.
                    </p>
                </div>
            </div>

            <form onSubmit={handleSave}>
                <div className="settings-card">
                    <h3 className="section-title"><i className="fab fa-whatsapp" style={{ color: '#25D366' }}></i> WhatsApp OTP</h3>
                    <p className="input-hint" style={{ marginBottom: '1rem' }}>
                        Used first when a guest requests an OTP — falls back to SMS automatically if WhatsApp isn't configured or fails.
                    </p>
                    <div className="form-grid">
                        <div className="form-group-modern">
                            <label>Provider</label>
                            <select
                                value={formData.whatsapp.provider}
                                onChange={e => setFormData(p => ({ ...p, whatsapp: { ...p.whatsapp, provider: e.target.value } }))}
                            >
                                <option value="">Disabled</option>
                                <option value="meta">Meta WhatsApp Cloud API (official)</option>
                                <option value="msg91">MSG91 WhatsApp</option>
                            </select>
                        </div>
                    </div>

                    {formData.whatsapp.provider === 'meta' && (
                        <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                            <div className="form-group-modern">
                                <label>Phone Number ID</label>
                                <input
                                    value={formData.whatsapp.meta.phoneId}
                                    onChange={e => setFormData(p => ({ ...p, whatsapp: { ...p.whatsapp, meta: { ...p.whatsapp.meta, phoneId: e.target.value } } }))}
                                    placeholder="e.g. 109876543210987"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="form-group-modern">
                                <label>Access Token</label>
                                <input
                                    type="password"
                                    value={formData.whatsapp.meta.accessToken}
                                    onChange={e => setFormData(p => ({ ...p, whatsapp: { ...p.whatsapp, meta: { ...p.whatsapp.meta, accessToken: e.target.value } } }))}
                                    placeholder={masked.metaAccessToken ? '•••••••• (saved — leave blank to keep)' : 'Permanent system-user access token'}
                                    autoComplete="new-password"
                                />
                            </div>
                            <div className="form-group-modern">
                                <label>Template Name</label>
                                <input
                                    value={formData.whatsapp.meta.templateName}
                                    onChange={e => setFormData(p => ({ ...p, whatsapp: { ...p.whatsapp, meta: { ...p.whatsapp.meta, templateName: e.target.value } } }))}
                                    placeholder="otp_login"
                                />
                            </div>
                            <div className="form-group-modern">
                                <label>Template Language</label>
                                <input
                                    value={formData.whatsapp.meta.templateLang}
                                    onChange={e => setFormData(p => ({ ...p, whatsapp: { ...p.whatsapp, meta: { ...p.whatsapp.meta, templateLang: e.target.value } } }))}
                                    placeholder="en_US"
                                />
                            </div>
                        </div>
                    )}

                    {formData.whatsapp.provider === 'msg91' && (
                        <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                            <div className="form-group-modern">
                                <label>WhatsApp Number (MSG91-integrated)</label>
                                <input
                                    value={formData.whatsapp.msg91.whatsappNumber}
                                    onChange={e => setFormData(p => ({ ...p, whatsapp: { ...p.whatsapp, msg91: { ...p.whatsapp.msg91, whatsappNumber: e.target.value } } }))}
                                    placeholder="91XXXXXXXXXX"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="form-group-modern">
                                <label>Template Name</label>
                                <input
                                    value={formData.whatsapp.msg91.templateName}
                                    onChange={e => setFormData(p => ({ ...p, whatsapp: { ...p.whatsapp, msg91: { ...p.whatsapp.msg91, templateName: e.target.value } } }))}
                                    placeholder="otp_verification"
                                />
                            </div>
                            <div className="form-group-modern">
                                <label>Template Language</label>
                                <input
                                    value={formData.whatsapp.msg91.templateLang}
                                    onChange={e => setFormData(p => ({ ...p, whatsapp: { ...p.whatsapp, msg91: { ...p.whatsapp.msg91, templateLang: e.target.value } } }))}
                                    placeholder="en"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="settings-card">
                    <h3 className="section-title"><i className="fas fa-sms" style={{ color: '#14263f' }}></i> SMS OTP (fallback)</h3>
                    <div className="form-grid">
                        <div className="form-group-modern">
                            <label>Provider</label>
                            <select
                                value={formData.sms.provider}
                                onChange={e => setFormData(p => ({ ...p, sms: { ...p.sms, provider: e.target.value } }))}
                            >
                                <option value="">Disabled</option>
                                <option value="msg91">MSG91 SMS</option>
                                <option value="twilio">Twilio</option>
                                <option value="ask4sms">Ask4SMS (India DLT)</option>
                            </select>
                        </div>
                    </div>

                    {formData.sms.provider === 'msg91' && (
                        <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                            <div className="form-group-modern">
                                <label>Template ID</label>
                                <input
                                    value={formData.sms.msg91.templateId}
                                    onChange={e => setFormData(p => ({ ...p, sms: { ...p.sms, msg91: { ...p.sms.msg91, templateId: e.target.value } } }))}
                                    autoComplete="off"
                                />
                            </div>
                            <div className="form-group-modern">
                                <label>Sender ID</label>
                                <input
                                    value={formData.sms.msg91.senderId}
                                    onChange={e => setFormData(p => ({ ...p, sms: { ...p.sms, msg91: { ...p.sms.msg91, senderId: e.target.value } } }))}
                                    placeholder="HTWIFI"
                                />
                            </div>
                        </div>
                    )}

                    {formData.sms.provider === 'twilio' && (
                        <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                            <div className="form-group-modern">
                                <label>Account SID</label>
                                <input
                                    value={formData.sms.twilio.accountSid}
                                    onChange={e => setFormData(p => ({ ...p, sms: { ...p.sms, twilio: { ...p.sms.twilio, accountSid: e.target.value } } }))}
                                    autoComplete="off"
                                />
                            </div>
                            <div className="form-group-modern">
                                <label>Auth Token</label>
                                <input
                                    type="password"
                                    value={formData.sms.twilio.authToken}
                                    onChange={e => setFormData(p => ({ ...p, sms: { ...p.sms, twilio: { ...p.sms.twilio, authToken: e.target.value } } }))}
                                    placeholder={masked.twilioAuthToken ? '•••••••• (saved — leave blank to keep)' : ''}
                                    autoComplete="new-password"
                                />
                            </div>
                            <div className="form-group-modern">
                                <label>Phone Number</label>
                                <input
                                    value={formData.sms.twilio.phoneNumber}
                                    onChange={e => setFormData(p => ({ ...p, sms: { ...p.sms, twilio: { ...p.sms.twilio, phoneNumber: e.target.value } } }))}
                                    placeholder="+1XXXXXXXXXX"
                                />
                            </div>
                        </div>
                    )}

                    {formData.sms.provider === 'ask4sms' && (() => {
                        const a = formData.sms.ask4sms;
                        const setA = (key) => (e) => setFormData(p => ({ ...p, sms: { ...p.sms, ask4sms: { ...p.sms.ask4sms, [key]: e.target.value } } }));
                        return (
                            <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                                <div className="form-group-modern full-width">
                                    <label>API URL</label>
                                    <input value={a.baseUrl} onChange={setA('baseUrl')} placeholder="http://api.ask4sms.in/sms/1/text/query" autoComplete="off" />
                                </div>
                                <div className="form-group-modern">
                                    <label>Username</label>
                                    <input value={a.username} onChange={setA('username')} autoComplete="off" />
                                </div>
                                <div className="form-group-modern">
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        value={a.password}
                                        onChange={setA('password')}
                                        placeholder={masked.ask4smsPassword ? '•••••••• (saved — leave blank to keep)' : ''}
                                        autoComplete="new-password"
                                    />
                                </div>
                                <div className="form-group-modern">
                                    <label>Sender ID (DLT header)</label>
                                    <input value={a.senderId} onChange={setA('senderId')} placeholder="e.g. HTWIFI" autoComplete="off" />
                                </div>
                                <div className="form-group-modern">
                                    <label>DLT Template ID</label>
                                    <input value={a.templateId} onChange={setA('templateId')} autoComplete="off" />
                                </div>
                                <div className="form-group-modern">
                                    <label>DLT Entity (PE) ID</label>
                                    <input value={a.peId} onChange={setA('peId')} autoComplete="off" />
                                </div>
                                <div className="form-group-modern">
                                    <label>DLT Telemarketer (TM) ID</label>
                                    <input value={a.tmId} onChange={setA('tmId')} autoComplete="off" />
                                </div>
                                <div className="form-group-modern full-width">
                                    <label>Message text <span style={{ fontWeight: 400, color: '#6b7280' }}>(the DLT-approved template, word for word — {'{otp}'} where the code goes)</span></label>
                                    <textarea
                                        rows={3}
                                        value={a.messageTemplate}
                                        onChange={setA('messageTemplate')}
                                        placeholder="Copy the approved template from your DLT portal and put {otp} in place of its variable"
                                        required
                                    />
                                    {!a.messageTemplate?.trim() && (
                                        <p className="input-hint" style={{ color: '#b91c1c' }}>
                                            Required — without it no OTP SMS can be sent.
                                        </p>
                                    )}
                                    <p className="input-hint">
                                        Operators drop an SMS whose text differs from the approved template in any way — copy it exactly.
                                        You can paste it as the DLT portal shows it: with one {'{#var#}'}, that is where the code goes.
                                        With several, put {'{otp}'} where the code goes and fill in the others yourself (e.g. 10 for the minutes).
                                    </p>
                                </div>
                            </div>
                        );
                    })()}

                    {(formData.whatsapp.provider === 'msg91' || formData.sms.provider === 'msg91') && (
                        <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                            <div className="form-group-modern full-width">
                                <label>MSG91 Account Authkey <span style={{ fontWeight: 400, color: '#6b7280' }}>(shared by MSG91 WhatsApp + SMS)</span></label>
                                <input
                                    type="password"
                                    value={formData.msg91AuthKey}
                                    onChange={e => setFormData(p => ({ ...p, msg91AuthKey: e.target.value }))}
                                    placeholder={masked.msg91AuthKey ? '•••••••• (saved — leave blank to keep)' : 'Your MSG91 account authkey'}
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="settings-actions">
                    <button type="submit" className="btn-primary-modern" disabled={saving}>
                        {saving ? (<><i className="fas fa-spinner fa-spin"></i> Saving...</>) : (<><i className="fas fa-save"></i> Save Settings</>)}
                    </button>
                </div>
            </form>

            <div className="settings-card">
                <h3 className="section-title"><i className="fas fa-vial"></i> Send Test OTP</h3>
                <p className="input-hint" style={{ marginBottom: '1rem' }}>
                    Sends a real OTP using whatever is currently saved above — the exact same code path a guest uses, so a successful test here means it will work for guests too.
                </p>
                <form onSubmit={handleTestOtp} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group-modern" style={{ flex: 1, minWidth: 200 }}>
                        <label>Mobile Number</label>
                        <input value={testMobile} onChange={e => setTestMobile(e.target.value)} placeholder="9606652996" autoComplete="off" />
                    </div>
                    <div className="form-group-modern">
                        <label>Channel</label>
                        <select value={testMethod} onChange={e => setTestMethod(e.target.value)}>
                            <option value="whatsapp">WhatsApp</option>
                            <option value="sms">SMS</option>
                        </select>
                    </div>
                    <button type="submit" className="btn-secondary-modern" disabled={testing}>
                        <i className={`fas ${testing ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i> {testing ? 'Sending...' : 'Send Test OTP'}
                    </button>
                </form>
                {testResult && (
                    <p role="status" className="input-hint" style={{ marginTop: '0.75rem', color: testResult.ok ? '#15803d' : '#b91c1c' }}>
                        <i className={`fas ${testResult.ok ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i> {testResult.message}
                    </p>
                )}
            </div>
        </div>
    );
};

export default Settings;
